import { NavLink } from "react-router-dom";
import { Box, Stack, UnstyledButton, Text, Tooltip, ActionIcon } from "@mantine/core";
import { useAuth } from "../../../hooks/core/useAuth";
import { useRolePermissions } from "../../../hooks/core/useRolePermissions";
import { useDevRoleOverride } from "../../../components/dev/RoleToggle";
import { hasPermission } from "../../../utils/rolePermissions";
import { useContext, useEffect } from "react";
import { SidebarContext } from "../index";
import { PortalVersion } from "../../../components/PortalVersion";
import {
  IconDashboard,
  IconUser,
  IconListDetails,
  IconUsers,
  IconCar,
  IconBuildingStore,
  IconMap,
  IconMap2,
  IconSettings,
  IconBuilding,
  IconHistory,
  IconBell,
  IconCoinPound,
  IconShieldLock,
  IconClipboardData,
  IconCalendarEvent,
  IconUserCog,
  IconBriefcase,
  IconLogout,
  IconTools,
  IconDatabase,
  IconWorld,
  IconKey,
  IconMessages,
  IconChevronLeft,
  IconChevronRight,
  IconActivity,
  IconFileSpreadsheet,
  IconVersions,
  IconCalendar,
  IconRubberStamp,
} from "@tabler/icons-react";

const navigationGroups = [
  {
    label: "Main",
    items: [
      {
        icon: IconDashboard,
        label: "Dashboard",
        path: "/",
        page: "dashboard",
        gradient: "from-blue-500 to-cyan-400",
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        icon: IconCar,
        label: "Bookings",
        path: "/bookings",
        page: "bookings",
        gradient: "from-purple-500 to-pink-400",
      },
      {
        icon: IconCalendarEvent,
        label: "Schedules",
        path: "/schedules",
        page: "schedules",
        gradient: "from-green-500 to-teal-400",
      },
      {
        icon: IconUsers,
        label: "Drivers",
        path: "/drivers",
        page: "drivers",
        gradient: "from-indigo-500 to-purple-400",
      },
      {
        icon: IconRubberStamp,
        label: "Compliance",
        path: "/compliance",
        page: "compliance",
        gradient: "from-indigo-500 to-purple-400",
      },
      {
        icon: IconCoinPound,
        label: "Finance",
        path: "/finance",
        page: "finance",
        gradient: "from-yellow-500 to-amber-400",
      },
    ],
  },
  {
    label: "Customers",
    items: [
      {
        icon: IconBuildingStore,
        label: "Accounts",
        path: "/accounts",
        page: "accounts",
        gradient: "from-teal-500 to-green-400",
      },
    ],
  },
  {
    label: "Administration",
    items: [
      // {
      //   icon: IconUsers,
      //   label: "Organization",
      //   path: "/organization",
      //   page: "organization",
      //   permission: "staff.profiles.view",
      //   gradient: "from-blue-500 to-indigo-400",
      // },
      {
        icon: IconCalendar,
        label: "Calendars",
        path: "/calendars",
        page: "calendars",
      },
      {
        icon: IconSettings,
        label: "Config",
        path: "/config-management",
        page: "configManagement",
        permission: "system.settings.view",
        gradient: "from-gray-500 to-slate-400",
      },
      {
        icon: IconMap,
        label: "Regions",
        path: "/regions",
        page: "regions",
        permission: "operations.vehicles.view",
        gradient: "from-cyan-500 to-blue-400",
      },
      {
        icon: IconFileSpreadsheet,
        label: "Export Area Settings",
        path: "/export-area-settings",
        page: "exportAreaSettings",
        gradient: "from-emerald-500 to-teal-400",
      },
    ],
  },
  {
    label: "Settings",
    items: [
      {
        icon: IconVersions,
        label: "App Versions",
        path: "/app-versions",
        page: "app-versions",
        gradient: "from-orange-500 to-red-400",
      },
    ],
  },
  {
    label: "Developer",
    items: [
      {
        icon: IconDatabase,
        label: "Migration",
        path: "/dev-tools/data-migration",
        page: "dataMigration",
        permission: "system.backups.view",
        gradient: "from-rose-500 to-pink-400",
      },
      {
        icon: IconTools,
        label: "Ghost Docs",
        path: "/dev-tools/ghost-documents",
        page: "ghostDocuments",
        permission: "system.audit.view",
        gradient: "from-stone-500 to-gray-400",
      },
    ],
  },
];

export function Sidebar() {
  const { staffProfile, user, isLoading, logout } = useAuth();
  const rolePermissions = useRolePermissions();
  const { overrideRole } = useDevRoleOverride();
  const { sidebarCollapsed: isCollapsed, setSidebarCollapsed: setIsCollapsed } = useContext(SidebarContext);

  const effectiveRole = overrideRole || staffProfile?.role;

  const filteredGroups = navigationGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.page === "finance" && !import.meta.env.VITE_API_GATEWAY_BASE_URL) {
          return false;
        }
        const hasViewPermission = hasPermission(effectiveRole, item.page, "view");
        return hasViewPermission;
      }),
    }))
    .filter((group) => group.items.length > 0); // Remove empty groups

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .sidebar-scroll::-webkit-scrollbar { width: 6px; }
      .sidebar-scroll::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.02); border-radius: 3px; }
      .sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.12); border-radius: 3px; transition: background 0.3s ease; }
      .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.18); }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <Box
      w="100%"
      h="100%"
      style={{
        background: "linear-gradient(180deg, rgba(8, 12, 24, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)",
        backdropFilter: "blur(24px)",
        borderRight: "1px solid rgba(255, 255, 255, 0.12)",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        style={{
          position: "absolute",
          top: 8,
          transform: isCollapsed ? "translateX(50%)" : "none",
          zIndex: 3,
        }}
      >
        <Tooltip label={isCollapsed ? "Expand" : "Collapse"} position="right" withArrow>
          <ActionIcon
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              background: "linear-gradient(135deg, var(--mantine-color-accent-5), var(--mantine-color-amber-5))",
              border: "2px solid rgba(8, 12, 24, 0.98)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.45)",
            }}
            radius="xl"
            size="md"
          >
            {isCollapsed ? <IconChevronRight size={16} color="white" /> : <IconChevronLeft size={16} color="white" />}
          </ActionIcon>
        </Tooltip>
      </Box>

      {/* Navigation Items */}
      <Box
        className="sidebar-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: isCollapsed ? "8px" : "8px 12px",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255, 255, 255, 0.12) transparent",
        }}
      >
        {filteredGroups.map((group) => (
          <Box key={group.label} mb="lg">
            {!isCollapsed && (
              <Text
                size="xs"
                fw={700}
                style={{
                  color: "rgba(255, 255, 255, 0.55)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  marginBottom: "8px",
                  marginLeft: "12px",
                }}
              >
                {group.label}
              </Text>
            )}
            <Stack gap={6}>
              {group.items.map((item) => (
                <Tooltip
                  key={item.path}
                  label={item.label}
                  position="right"
                  disabled={!isCollapsed}
                  withArrow
                  arrowSize={6}
                  transitionProps={{ transition: "fade", duration: 200 }}
                >
                  <UnstyledButton
                    component={NavLink}
                    to={item.path}
                    style={{ position: "relative", overflow: "hidden" }}
                    styles={() => ({
                      root: {
                        display: "flex",
                        alignItems: "center",
                        gap: isCollapsed ? 0 : 12,
                        padding: isCollapsed ? "10px" : "10px 12px",
                        borderRadius: "12px",
                        color: "rgba(255, 255, 255, 0.88)",
                        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        justifyContent: isCollapsed ? "center" : "flex-start",
                        outline: "none",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(135deg, var(--mantine-color-accent-5), var(--mantine-color-amber-5))",
                          opacity: 0,
                          transition: "opacity 0.22s ease",
                          borderRadius: "12px",
                        },
                        "&:hover": {
                          transform: "translateX(2px)",
                          color: "rgba(255, 255, 255, 0.98)",
                          "&::before": { opacity: 0.08 },
                          "& .icon-wrapper": { transform: "scale(1.06)" },
                        },
                        "&:focusVisible": {
                          boxShadow: "0 0 0 2px rgba(99,102,241,0.35)",
                        },
                        "&.active": {
                          color: "white",
                          fontWeight: 700,
                          "&::before": { opacity: 0.18 },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: "4px",
                            height: "70%",
                            background: "linear-gradient(180deg, #6366f1, #a855f7)",
                            borderRadius: "0 3px 3px 0",
                          },
                          "& .icon-wrapper": {
                            background:
                              "linear-gradient(135deg, rgba(255, 136, 31, 0.22) 0%, rgba(255, 174, 46, 0.22) 100%)",
                            transform: "scale(1.04)",
                          },
                        },
                      },
                    })}
                  >
                    <Box
                      className="icon-wrapper"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: "10px",
                        background: "rgba(255, 255, 255, 0.08)",
                        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      <item.icon size={18} stroke={1.6} color="rgba(255, 255, 255, 0.85)" />
                    </Box>
                    {!isCollapsed && (
                      <Text
                        size="sm"
                        fw={600}
                        style={{
                          letterSpacing: "0.02em",
                          color: "rgba(255,255,255,0.92)",
                        }}
                      >
                        {item.label}
                      </Text>
                    )}
                  </UnstyledButton>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        ))}
      </Box>

      {/* Version Display - Subtle */}
      <Box
        style={{
          marginTop: "auto",
          paddingTop: "4px",
        }}
      >
        <PortalVersion compact={isCollapsed} />
      </Box>

      {/* Bottom Section */}
      <Box
        style={{
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          padding: isCollapsed ? "12px 8px" : "12px",
          background: "rgba(255, 255, 255, 0.03)",
        }}
      >
        {/* Profile Link */}
        <Tooltip label="My Profile" position="right" disabled={!isCollapsed} withArrow>
          <UnstyledButton
            component={NavLink}
            to="/staff/profile"
            style={{
              display: "flex",
              alignItems: "center",
              gap: isCollapsed ? 0 : 12,
              padding: isCollapsed ? "10px" : "10px 12px",
              borderRadius: "12px",
              color: "rgba(255, 255, 255, 0.96)",
              background: "rgba(255, 255, 255, 0.06)",
              border: "1px solid rgba(255, 255, 255, 0.10)",
              transition: "all 0.2s ease",
              justifyContent: isCollapsed ? "center" : "flex-start",
              marginBottom: "8px",
            }}
            styles={() => ({
              root: {
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.10)",
                  color: "white",
                  transform: "translateY(-1px)",
                },
                "&:focusVisible": {
                  boxShadow: "0 0 0 2px rgba(99,102,241,0.35)",
                  outline: "none",
                },
              },
            })}
          >
            <IconUser size={18} stroke={1.6} color="rgba(255,255,255,0.95)" />
            {!isCollapsed && (
              <Text size="sm" fw={700} style={{ letterSpacing: "0.02em", color: "white" }}>
                My Profile
              </Text>
            )}
          </UnstyledButton>
        </Tooltip>

        {/* Logout Button */}
        <Tooltip label="Logout" position="right" disabled={!isCollapsed} withArrow>
          <UnstyledButton
            onClick={async () => {
              const result = await logout();
              if (result?.success) {
                window.location.href = "/auth";
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: isCollapsed ? 0 : 12,
              padding: isCollapsed ? "10px" : "10px 12px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, rgba(239,68,68,0.16), rgba(244,63,94,0.16))",
              border: "1px solid rgba(239,68,68,0.32)",
              color: "#fee2e2",
              transition: "all 0.2s ease",
              justifyContent: isCollapsed ? "center" : "flex-start",
            }}
            styles={() => ({
              root: {
                "&:hover": {
                  background: "linear-gradient(135deg, rgba(239,68,68,0.24), rgba(244,63,94,0.24))",
                  transform: "translateY(-1px)",
                  color: "#ffffff",
                },
                "&:focusVisible": {
                  boxShadow: "0 0 0 2px rgba(239,68,68,0.35)",
                  outline: "none",
                },
              },
            })}
          >
            <IconLogout size={18} stroke={1.6} color="#fecaca" />
            {!isCollapsed && (
              <Text size="sm" fw={700} style={{ letterSpacing: "0.02em", color: "white" }}>
                Logout
              </Text>
            )}
          </UnstyledButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default Sidebar;
